import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Clock, Globe, Send, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARD_ICONS = [Mail, Phone, Clock, Globe];

const Contact = () => {
  const { t, lang } = useLanguage();
  const cards = t("contact.cards");
  const tjenester = t("contact.tjenester");
  const emptyForm = { navn: "", firma: "", epost: "", telefon: "", tjeneste: tjenester[0], melding: "" };

  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, sprak: lang });
      toast.success(t("contact.toastSuccess"));
      setForm({ ...emptyForm, tjeneste: tjenester[0] });
    } catch (err) {
      toast.error(t("contact.toastError"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:border-sky-600 focus:outline-none";
  const labelCls = "font-mono text-xs uppercase tracking-widest text-slate-500";

  return (
    <section id="kontakt" className="scroll-mt-20 border-t border-slate-200 bg-slate-50 py-20 sm:py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700" data-testid="contact-badge">
              {t("contact.badge")}
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="contact-title">
              {t("contact.title")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{t("contact.intro")}</p>

            <div className="mt-10 space-y-4">
              {cards.map((c, i) => {
                const Icon = CARD_ICONS[i];
                const inner = (
                  <>
                    <div className="rounded-md border border-sky-100 bg-sky-50 p-3 text-sky-700">
                      <Icon size={18} />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{c.label}</p>
                      <p className="mt-0.5 text-sm font-semibold text-slate-800">{c.value}</p>
                    </div>
                  </>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} data-testid={c.testid} className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 transition-colors duration-200 hover:border-sky-600/50">
                    {inner}
                  </a>
                ) : (
                  <div key={c.label} data-testid={c.testid} className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-4">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="corner-frame rounded-lg border border-slate-200 bg-white p-7 shadow-md sm:p-10 lg:col-span-3" data-testid="contact-form">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-navn" className={labelCls}>{t("contact.labels.navn")}</label>
                <input id="cf-navn" required minLength={2} value={form.navn} onChange={update("navn")} data-testid="contact-input-name" className={inputCls} placeholder={t("contact.placeholders.navn")} />
              </div>
              <div>
                <label htmlFor="cf-firma" className={labelCls}>{t("contact.labels.firma")}</label>
                <input id="cf-firma" value={form.firma} onChange={update("firma")} data-testid="contact-input-company" className={inputCls} placeholder={t("contact.placeholders.firma")} />
              </div>
              <div>
                <label htmlFor="cf-epost" className={labelCls}>{t("contact.labels.epost")}</label>
                <input id="cf-epost" type="email" required value={form.epost} onChange={update("epost")} data-testid="contact-input-email" className={inputCls} placeholder={t("contact.placeholders.epost")} />
              </div>
              <div>
                <label htmlFor="cf-tlf" className={labelCls}>{t("contact.labels.telefon")}</label>
                <input id="cf-tlf" type="tel" value={form.telefon} onChange={update("telefon")} data-testid="contact-input-phone" className={inputCls} placeholder={t("contact.placeholders.telefon")} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-tjeneste" className={labelCls}>{t("contact.labels.tjeneste")}</label>
                <select id="cf-tjeneste" required value={form.tjeneste} onChange={update("tjeneste")} data-testid="contact-select-service" className={inputCls}>
                  {tjenester.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-melding" className={labelCls}>{t("contact.labels.melding")}</label>
                <textarea
                  id="cf-melding"
                  required
                  minLength={5}
                  rows={5}
                  value={form.melding}
                  onChange={update("melding")}
                  data-testid="contact-input-message"
                  className={`${inputCls} resize-y`}
                  placeholder={t("contact.placeholders.melding")}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? t("contact.sending") : t("contact.submit")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
